import { WorkflowAgent, WorkflowSession, SessionMessage } from "./types";

/**
 * Set to true to use mock data instead of real API calls.
 * Useful for local development when the backend is not running.
 */
export const USE_MOCK_DATA = false;

export const mockAgents: WorkflowAgent[] = [
  {
    id: "a1b2c3d4-0001-4000-8000-000000000001",
    name: "Setu (Internal)",
    slug: "setu-internal",
    description:
      "AI engineering assistant for the Altimate team. Handles code reviews, Jira tasks, and engineering workflows.",
    instructions:
      "You are Setu, an AI engineering assistant for the Altimate team. You help with code reviews, Jira task management, and engineering workflows.",
    model: "claude-sonnet-4-20250514",
    tools: ["code-review", "jira-ops", "slack-bot", "git-workflow"],
    enabled: true,
    created_at: "2025-02-10T08:00:00Z",
  },
  {
    id: "a1b2c3d4-0002-4000-8000-000000000002",
    name: "DataMate Pro",
    slug: "datamate-pro",
    description: "Data catalog assistant with SQL, lineage, and schema exploration capabilities.",
    model: "gpt-4o",
    tools: ["sql-assist", "data-catalog", "lineage"],
    enabled: true,
    created_at: "2025-01-15T12:00:00Z",
  },
  {
    id: "a1b2c3d4-0003-4000-8000-000000000003",
    name: "CostBot",
    slug: "costbot",
    description: "Cloud cost analysis and alerting agent.",
    tools: ["cost-analysis", "alerts"],
    enabled: false,
    created_at: "2025-01-20T09:00:00Z",
  },
  {
    id: "a1b2c3d4-0004-4000-8000-000000000004",
    name: "GovernanceGuard",
    slug: "governance-guard",
    description: "Policy checks, access reviews, and compliance monitoring.",
    model: "claude-sonnet-4-20250514",
    tools: ["policy-check", "access-review", "compliance"],
    enabled: true,
    created_at: "2025-02-01T14:00:00Z",
  },
  {
    id: "a1b2c3d4-0005-4000-8000-000000000005",
    name: "QueryOptimizer",
    slug: "query-optimizer",
    description: "Analyzes and optimizes SQL queries for performance.",
    tools: ["query-optimization", "index-advisor"],
    enabled: true,
    created_at: "2024-12-01T10:00:00Z",
  },
];

export const mockSessions: WorkflowSession[] = [
  {
    id: "s1b2c3d4-0001-4000-8000-000000000001",
    title: "Review PR #2305 - tenant config changes",
    description: "Code review and migration verification for PR #2305",
    status: "active",
    channel: "#cc-workflow",
    priority: "high",
    current_agent_id: "a1b2c3d4-0001-4000-8000-000000000001",
    created_by: "john@altimate.ai",
    assignee: "setu-internal",
    next_action: "Awaiting PR approval",
    created_at: new Date(Date.now() - 1800000).toISOString(),
  },
  {
    id: "s1b2c3d4-0002-4000-8000-000000000002",
    title: "Create Jira ticket for workflow frontend",
    status: "completed",
    channel: "#engineering",
    priority: "medium",
    current_agent_id: "a1b2c3d4-0001-4000-8000-000000000001",
    created_by: "jane@altimate.ai",
    completed_at: new Date(Date.now() - 5400000).toISOString(),
    created_at: new Date(Date.now() - 7200000).toISOString(),
  },
  {
    id: "s1b2c3d4-0003-4000-8000-000000000003",
    title: "Customer revenue data exploration",
    status: "active",
    channel: "#data-team",
    priority: "medium",
    current_agent_id: "a1b2c3d4-0002-4000-8000-000000000002",
    created_by: "analyst@altimate.ai",
    next_action: "Review generated SQL queries",
    created_at: new Date(Date.now() - 3600000).toISOString(),
  },
  {
    id: "s1b2c3d4-0004-4000-8000-000000000004",
    title: "Q1 2025 access review",
    status: "active",
    channel: "#compliance",
    priority: "urgent",
    current_agent_id: "a1b2c3d4-0004-4000-8000-000000000004",
    created_by: "security@altimate.ai",
    next_action: "Retry identity provider connection",
    created_at: new Date(Date.now() - 900000).toISOString(),
  },
  {
    id: "s1b2c3d4-0005-4000-8000-000000000005",
    title: "Optimize slow transaction query",
    status: "completed",
    channel: "#data-team",
    priority: "low",
    current_agent_id: "a1b2c3d4-0005-4000-8000-000000000005",
    completed_at: new Date(Date.now() - 4800000).toISOString(),
    created_at: new Date(Date.now() - 5400000).toISOString(),
  },
  {
    id: "s1b2c3d4-0006-4000-8000-000000000006",
    title: "Monthly revenue report for executive team",
    status: "completed",
    channel: "#analytics",
    priority: "high",
    current_agent_id: "a1b2c3d4-0002-4000-8000-000000000002",
    completed_at: new Date(Date.now() - 9000000).toISOString(),
    created_at: new Date(Date.now() - 10800000).toISOString(),
  },
  {
    id: "s1b2c3d4-0007-4000-8000-000000000007",
    title: "Deploy workflow feature to staging",
    status: "active",
    channel: "#cc-meta",
    priority: "medium",
    current_agent_id: "a1b2c3d4-0001-4000-8000-000000000001",
    created_by: "dev@altimate.ai",
    next_action: "Define deployment steps",
    created_at: new Date(Date.now() - 300000).toISOString(),
  },
  {
    id: "s1b2c3d4-0008-4000-8000-000000000008",
    title: "Investigate stale lineage edges",
    status: "active",
    channel: "#data-team",
    priority: "high",
    current_agent_id: "a1b2c3d4-0002-4000-8000-000000000002",
    created_by: "analyst@altimate.ai",
    next_action: "Re-run lineage scan after fix",
    created_at: new Date(Date.now() - 86400000).toISOString(),
  },
];

export const mockSessionMessages: Record<string, SessionMessage[]> = {
  "s1b2c3d4-0001-4000-8000-000000000001": [
    {
      id: 1,
      role: "user",
      content: "Review PR #2305 for the tenant config changes",
      created_at: new Date(Date.now() - 1800000).toISOString(),
    },
    {
      id: 2,
      role: "assistant",
      content: "I'll review PR #2305. Let me fetch the changes and analyze them...",
      created_at: new Date(Date.now() - 1795000).toISOString(),
    },
    {
      id: 3,
      role: "assistant",
      content:
        "PR #2305 looks good. The tenant configuration changes follow existing patterns. I've left 2 inline suggestions for minor improvements. LGTM overall.",
      created_at: new Date(Date.now() - 1700000).toISOString(),
    },
    {
      id: 4,
      role: "user",
      content: "Thanks! Can you also check if the database migrations are included?",
      created_at: new Date(Date.now() - 1650000).toISOString(),
    },
    {
      id: 5,
      role: "assistant",
      content:
        "I see the migrations in `/migrations/` folder. The new tenant_config table looks properly structured with appropriate indexes.",
      created_at: new Date(Date.now() - 1600000).toISOString(),
    },
  ],
  "s1b2c3d4-0002-4000-8000-000000000002": [
    {
      id: 6,
      role: "user",
      content: "Create a Jira ticket for the new workflow frontend",
      created_at: new Date(Date.now() - 7200000).toISOString(),
    },
    {
      id: 7,
      role: "assistant",
      content:
        "Created AI-5757: [Workflow][Frontend] Workflow Tab MVP. Assigned to the frontend team with priority High.",
      created_at: new Date(Date.now() - 7180000).toISOString(),
    },
  ],
  "s1b2c3d4-0007-4000-8000-000000000007": [
    {
      id: 24,
      role: "user",
      content: "Can you help me deploy the new workflow feature to staging?",
      created_at: new Date(Date.now() - 300000).toISOString(),
    },
    {
      id: 25,
      role: "assistant",
      content:
        "Sure! Let me check the current staging environment status and run the deployment pipeline...",
      created_at: new Date(Date.now() - 280000).toISOString(),
    },
    {
      id: 26,
      role: "assistant",
      content: "Staging deployment initiated. Build #1247 is currently running. ETA: 3-5 minutes.",
      created_at: new Date(Date.now() - 240000).toISOString(),
    },
  ],
};
